import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useLocale } from "../lib/useLocale";
import api from "../lib/api";
import { ArrowRight, Clock } from "lucide-react";

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }); }
  catch { return ""; }
};

export default function Blog() {
  const { path: lp } = useLocale();
  const [posts, setPosts] = useState([]);

  useEffect(() => { api.get("/blog").then((r) => setPosts(r.data)).catch(() => {}); }, []);

  const [featured, ...rest] = posts;

  return (
    <div data-testid="blog-page">
      <Helmet>
        <title>The Rokert Journal | Ayurveda, Longevity & Wellness</title>
        <meta name="description" content="Physician-led insight on Ayurvedic detox, longevity and mindful travel from Rokert Retreats — the wellness journal for a longer, richer life." />
        <meta property="og:title" content="The Rokert Journal" />
        <meta property="og:description" content="Ayurveda, longevity and mindful travel — insight from Rokert Retreats." />
      </Helmet>

      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-32 md:pt-40 pb-14">
        <div className="hairline mb-7" />
        <span className="overline">The Rokert Journal</span>
        <h1 className="font-heading text-5xl md:text-7xl leading-[0.95] mt-4 text-rr-ivory">Wellness, written slowly.</h1>
        <p className="text-rr-ivory/60 text-base md:text-lg max-w-xl mt-6 leading-relaxed">Physician-led perspective on Ayurvedic detox, longevity and the art of unhurried travel.</p>
      </section>

      {featured && (
        <section className="max-w-7xl mx-auto px-6 md:px-10 pb-16">
          <Link to={lp(`/blog/${featured.slug}`)} data-testid="blog-featured" className="group grid md:grid-cols-2 gap-8 md:gap-12 items-center">
            <div className="relative h-[320px] md:h-[440px] overflow-hidden rounded-xl">
              <img src={featured.cover_image} alt={featured.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] group-hover:scale-105" />
              <div className="absolute inset-0 hero-overlay-soft" />
            </div>
            <div>
              <div className="flex items-center gap-3 text-rr-muted text-xs uppercase tracking-wider">
                <span>{fmtDate(featured.created_at)}</span>
                <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> {featured.read_minutes} min</span>
              </div>
              <h2 className="font-heading text-3xl md:text-5xl text-rr-ivory mt-4 leading-tight group-hover:text-rr-gold transition-colors">{featured.title}</h2>
              <p className="text-rr-ivory/65 text-base mt-4 leading-relaxed">{featured.excerpt}</p>
              <span className="inline-flex items-center gap-2 text-rr-gold text-xs uppercase tracking-wider mt-7">Read article <ArrowRight className="w-4 h-4" strokeWidth={1.8} /></span>
            </div>
          </Link>
        </section>
      )}

      <section className="max-w-7xl mx-auto px-6 md:px-10 pb-28">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {rest.map((p) => (
            <Link key={p.slug} to={lp(`/blog/${p.slug}`)} data-testid={`blog-card-${p.slug}`} className="group card-lift rounded-xl overflow-hidden border border-rr-line bg-rr-surface/30">
              <div className="relative h-[220px] overflow-hidden">
                <img src={p.cover_image} alt={p.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] group-hover:scale-105" />
              </div>
              <div className="p-6">
                <div className="flex items-center gap-3 text-rr-muted text-xs uppercase tracking-wider">
                  <span>{fmtDate(p.created_at)}</span>
                  <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> {p.read_minutes} min</span>
                </div>
                <h3 className="font-heading text-2xl text-rr-ivory mt-3 leading-snug group-hover:text-rr-gold transition-colors">{p.title}</h3>
                <p className="text-rr-ivory/55 text-sm mt-3 leading-relaxed line-clamp-3">{p.excerpt}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
